import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useNavigate, useParams } from 'react-router-dom'
import {
  deactivateFreeTrial,
  getProducts,
} from '../../modules/productsListing/action'
import { useDispatch, useSelector } from '../../store/hooks'
import { useStyles } from './FreeTrialLabraUI.styles'
import { Product } from '../../modules/productsListing/types'
import { Dimensions } from './Dimensions/Dimensions'
import { Eula } from '../PrivateOfferForm/PrivateOfferCreation/Eula'
import { getEulaOption } from '../PrivateOfferForm/PrivateOfferCreation/utils'
import {
  Button,
  ModalConfirm,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import {
  activateFreeTrial,
  updateEulaType,
  updateOnReview,
} from '../../modules/freeTrials/action'
import { EulaType } from '../../modules/privateOffer/types'
import { DetailedFreeTrial } from './DetailedFreeTrial/DetailedFreeTrial'
import { FreeTrials, FreeTrialStatus } from '../../modules/freeTrials/types'
import { Typography } from '@mui/material'
import { defaultNow } from '../../common/utils/dateTimeNow'
import { KeyValue } from '../../modules/privateOffer/privateOfferCreation/types'
import { uploadFiles } from '../../modules/uploadEulaFiles/action'
import { bucketName } from '../../common/utils/constants'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import {
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
} from '@labrav/common'

export const FreeTrialLabraUI: React.FC = () => {
  // Sync route params to selectedAccountIds for FLYOUT
  useSyncFlyoutRouteParams()

  const { classes } = useStyles()
  const dispatch = useDispatch()
  const cloudMarketplaceProductListing = useCurrentCloudProvider()
  const partnerId = useSelector<string>(
    state => state.AccountData.user.partnerData?.partnerId || ''
  )
  const { productId } = useParams<{ productId: string }>() as {
    productId: string
  }

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const product =
    products.find(product => product.productId === productId) || ({} as Product)

  const eulaType = useSelector<string>(
    state => state.freeTrials[productId]?.eulaType || ''
  )
  const [openModal, setOpenModal] = useState(false)
  const [selectedEulaFiles, setSelectedEulaFiles] = useState<File[]>([])
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const isQuantityZero = useSelector<boolean>(state =>
    (state.freeTrials[productId]?.productDimensions || []).some(
      dimension =>
        isEmpty(dimension.freeTrial?.quantity) ||
        Number(dimension.freeTrial?.quantity) === 0
    )
  )
  const onReview = useSelector<boolean>(
    state => state.freeTrials[productId]?.onReview || false
  )
  const isNumberOfDaysValid = useSelector<boolean>(state => {
    const days = state.freeTrials[productId]?.freeTrial.freeTrialExpiresInDays
    return !isEmpty(days) && Number(days) >= 7 && Number(days) <= 90
  })
  const productFreeTrial = useSelector<FreeTrials>(
    state => state.freeTrials[productId]
  )
  const isDisabled =
    loading ||
    isQuantityZero ||
    !isNumberOfDaysValid ||
    (eulaType === EulaType.CUSTOM && isEmpty(selectedEulaFiles)) ||
    isEmpty(productFreeTrial?.productDimensions) ||
    isEmpty(eulaType)

  const freeTrialActivated =
    product?.freeTrial?.freeTrialStatus ===
      FreeTrialStatus.FREE_TRIAL_CREATED || false
  const navigate = useNavigate()
  useEffect(() => {
    if (!isEmpty(partnerId)) {
      /* istanbul ignore next */
      if (isEmpty(product)) {
        dispatch(getProducts(partnerId, cloudMarketplaceProductListing))
      }
    }
  }, [partnerId])
  const selectedDimensions = useMemo(
    () =>
      (
        (product?.productDimensions || []).filter(
          dimension => dimension?.freeTrial?.freeTrial
        ) || []
      ).map(dimension => ({
        productDimensionId: dimension.id,
        freeTrial: {
          freeTrial: dimension?.freeTrial?.freeTrial || false,
          quantity: Number(dimension?.freeTrial?.quantity),
        },
      })),
    [product]
  )
  const selectedFreeTrialProduct = useMemo(
    () => ({
      productId: product?.productId || '',
      eulaType: product?.freeTrial?.eulaType || '',
      eulaFile: product?.freeTrial?.eulaFiles || [],
      freeTrial: {
        ...product?.freeTrial,
      },
      productDimensions: [...selectedDimensions],
    }),
    [selectedDimensions]
  )

  const [disableUpload, setDisabledUpload] = useState<boolean>(false)
  const uploadFile = useCallback(async () => {
    if (!isEmpty(selectedEulaFiles)) {
      await dispatch(
        uploadFiles({
          partnerId,
          productId,
          filesToBeUploaded: selectedEulaFiles,
          assetType: 'eula_files',
          folder: 'free_trial_eula',
          defaultNow,
          bucketName,
        })
      )
    } else {
      await dispatch(updateOnReview(productId, true))
    }
  }, [productId, selectedEulaFiles, partnerId, onReview])
  const handleCancel = useCallback(async () => {
    await dispatch(updateOnReview(productId, false))
  }, [partnerId, productId, onReview])
  const redirect = useCallback(() => {
    navigate('/flyout/products-listing')
  }, [productId, partnerId])
  const handleActivate = useCallback(async () => {
    await dispatch(activateFreeTrial(partnerId, productId))
    await redirect()
  }, [productId, partnerId])
  useEffect(() => {
    if (selectedEulaFiles.length >= 5) {
      setDisabledUpload(true)
    } else {
      setDisabledUpload(false)
    }
  }, [selectedEulaFiles])
  const setEulaFile = useCallback(
    (files: File[]) => {
      /* istanbul ignore next */
      setSelectedEulaFiles(files)
    },
    [selectedEulaFiles, productId]
  )
  const handleDeactivate = useCallback(() => {
    setOpenModal(true)
  }, [product])
  const handleAccept = useCallback(async () => {
    handleClose()
    await dispatch(
      deactivateFreeTrial({
        labraFreeTrialId: product?.freeTrial?.labraFreeTrialId || '',
        partnerId,
        productId: product?.productId || '',
        cloudMarketplace: product?.cloudMarketplace || '',
      })
    )
    await redirect()
  }, [product])
  const goBackToPreviousPage = () => {
    navigate(-1)
  }
  const handleChange = useCallback(
    async (data: KeyValue) => {
      await dispatch(updateEulaType(data.value as string, productId))
    },
    [product.productId]
  )
  const noOps = useCallback(() => () => void 0, [])
  const handleClose = useCallback(() => setOpenModal(false), [product])
  if (isEmpty(product)) {
    return <div data-testid="empty-free-trial-container"></div>
  } else {
    if (
      product?.freeTrial?.freeTrialStatus ===
        FreeTrialStatus.FREE_TRIAL_CREATION_IN_PROGRESS ||
      product?.freeTrial?.freeTrialStatus ===
        FreeTrialStatus.FREE_TRIAL_DEACTIVATION_IN_PROGRESS
    ) {
      return <div data-testid="empty-free-trial-container-on-progress"></div>
    } else if (freeTrialActivated) {
      return (
        <div data-testid="deactivate-container">
          <ReviewComponent
            product={product}
            productFreeTrial={selectedFreeTrialProduct}
            handleAccept={handleDeactivate}
            acceptLabel={'Dectivate Trial'}
            isDisabled={loading}
            goBackToPreviousPage={goBackToPreviousPage}
          />
          <ModalConfirm
            title={'Deactivate Free Trial'}
            subTitle={''}
            content={
              <Typography className={classes.modalContent}>
                {'Do you want to deactivate free trial?'}
              </Typography>
            }
            open={openModal}
            titleAcceptButton={'Confirm'}
            onAccept={handleAccept}
            onDecline={handleClose}
            variant={ModalSizeVariants.small}
          />
        </div>
      )
    } else if (onReview) {
      return (
        <ReviewComponent
          product={product}
          productFreeTrial={productFreeTrial}
          handleCancel={handleCancel}
          handleAccept={handleActivate}
          acceptLabel={'Activate Trial'}
          isDisabled={loading}
        />
      )
    } else {
      return (
        <div className={classes.root}>
          <Dimensions product={product} />
          <Eula
            eulaType={eulaType as EulaType}
            selectedEulaFiles={selectedEulaFiles}
            setEulaFile={setEulaFile}
            disableUpload={disableUpload}
            handleChange={handleChange}
            fileUploadHeading={'Service agreement documents'}
            fileUploadSubHeading={
              'Upload your custom EULA(s) for this specific free trial'
            }
            heading={'Select EULA'}
            subHeading={'EULA version'}
            tooltipLabel={'Select EULA or add your own custom EULA files.'}
            deleteLink={noOps}
            eulaOptions={getEulaOption(
              product?.productType || PlanType.SAAS_CONTRACT
            )}
            showFileUploadForOptions={[
              EulaType.STANDARD,
              EulaType.PUBLIC,
              EulaType.CUSTOM,
            ]}
          />
          <div className={classes.buttonContainer}>
            <Button
              onClick={uploadFile}
              className={classes.button}
              data-testid="submit-button"
              disabled={isDisabled}
            >
              Submit
            </Button>
          </div>
        </div>
      )
    }
  }
}

export type ReviewComponentProps = {
  product: Product
  productFreeTrial: FreeTrials
  handleCancel?: () => void
  handleAccept: () => void
  acceptLabel: string
  isDisabled: boolean
  goBackToPreviousPage?: () => void
}
export const ReviewComponent = React.memo(
  ({
    product,
    productFreeTrial,
    handleCancel,
    handleAccept,
    acceptLabel,
    isDisabled,
    goBackToPreviousPage,
  }: ReviewComponentProps) => {
    const { classes } = useStyles()
    return (
      <div className={classes.reviewRoot} data-testid="review-container">
        <DetailedFreeTrial
          productFreeTrial={productFreeTrial}
          product={product}
        />
        <div className={classes.reviewButtonContainer}>
          {handleCancel ? (
            <Button
              styleType="secondary"
              onClick={handleCancel}
              className={classes.cancelButton}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
          ) : null}

          {goBackToPreviousPage && (
            <Button
              styleType="secondary"
              onClick={goBackToPreviousPage}
              data-testid="back-to-prev-button"
              className={classes.cancelButton}
            >
              {'back'}
            </Button>
          )}

          <Button
            styleType="primary"
            onClick={handleAccept}
            data-testid="accept-button"
            disabled={isDisabled}
          >
            {acceptLabel}
          </Button>
        </div>
      </div>
    )
  }
)
